import { useEffect, useState } from 'react';
import { AddUser } from './AddUser';
import { Sheet } from '@/components/ui';
import { Button } from '@curtin-dxp/curtin-ui';
import { ShieldAlert, Users } from 'lucide-react';
import { useHandleError, useUserAccessStats } from '@/hooks';
import { Loader } from '@/components/Common';
import { AddAdmin, UserAccessSummary } from '@/components';
import { useAwmsStore } from '@/store';
import { UserAccessDetails } from './UserAccessDetails';
import { useUserAccessDetails } from '@/hooks/use-user-access-details';

export const UserAccessManagement = () => {
  const [adminResetKey, setAdminResetKey] = useState(0);
  const [openSheet, setOpenSheet] = useState(false);
  const [openAdminSheet, setOpenAdminSheet] = useState(false);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const handleError = useHandleError();
  const {
    data: userAccessStats,
    isLoading: isUserAccessStatsLoading,
    error: userAccessStatsError,
  } = useUserAccessStats();
  const {
    data: usersAccessDetails,
    isLoading: isUsersAccessDetailsLoading,
    error: usersAccessDetailsError,
  } = useUserAccessDetails();

  const handleSheetChange = (open: boolean) => {
    if (!open) {
      setUserId(undefined);
      setOpenSheet(false);
    }
  };
  const handleAdminSheetChange = (open: boolean) => {
    if (!open) {
      setOpenAdminSheet(false);
      setAdminResetKey(adminResetKey + 1);
    }
  };
  const openAddUserSheet = (userId: string) => {
    setOpenSheet(true);
    setUserId(userId);
  };

  useEffect(() => {
    if (userAccessStatsError || usersAccessDetailsError) {
      console.error('Error fetching user access data:', {
        isUserAccessStatsError: userAccessStatsError,
        isUserAccessDetailsError: usersAccessDetailsError,
      });
    }
    handleError([userAccessStatsError, usersAccessDetailsError]);
  }, [handleError, userAccessStatsError, usersAccessDetailsError]);

  useEffect(() => {
    setIsPageLoading(isUsersAccessDetailsLoading || isUserAccessStatsLoading);
  }, [isPageLoading, isUsersAccessDetailsLoading, isUserAccessStatsLoading]);

  if (isPageLoading) return <Loader />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="pl-8">{userAccessStats && <UserAccessSummary userAccessStats={userAccessStats} />}</div>
        <div className="flex flex-col items-center justify-between gap-6">
          <div className="pl-8">
            <Button
              variant="outline-secondary"
              className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
              aria-label="Open Add Edit User Access"
              onClick={() => setOpenSheet(true)}
            >
              User access
              <Users className="ml-[10px] h-6 w-6" />
            </Button>
          </div>
          <div className="pl-8">
            <Button
              variant="outline-secondary"
              className="h-10 border-black bg-black text-white hover:bg-transparent hover:text-black focus:outline-none focus-visible:ring-1
                focus-visible:ring-inset focus-visible:ring-white focus-visible:ring-offset-0"
              aria-label="Open Add Remove Admin Users"
              onClick={() => setOpenAdminSheet(true)}
            >
              Admin users
              <ShieldAlert className="ml-[10px] h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
      <div className="pl-8">
        {usersAccessDetails && (
          <UserAccessDetails userAccessDetails={usersAccessDetails} openAddUserSheet={openAddUserSheet} />
        )}
      </div>

      <Sheet open={openSheet} onOpenChange={handleSheetChange}>
        <AddUser userId={userId} setUserId={setUserId} />
      </Sheet>
      <Sheet open={openAdminSheet} onOpenChange={handleAdminSheetChange}>
        <AddAdmin key={adminResetKey} />
      </Sheet>
    </div>
  );
};
